/**
 * EditorView — the §5.2 双模式编辑器 (dual-mode editor).
 *
 * A SegmentedControl toggles between two views over the *same* source string:
 *  - 代码 (code): a monospace Textarea bound to the raw `.ls` source. This is the
 *    single source of truth — editing it calls `onChange`.
 *  - 卡片 (card): a DIRECTLY EDITABLE render of the source, parsed with the
 *    `parseLsBlocks` display parser. Each significant beat becomes a colored row
 *    with a kind Badge (tinted via the `--kind-*` tokens). Editable leaf kinds
 *    expose inline field editors; compound / structural kinds stay read-only.
 *
 * Round-trip contract (卡片 → 源码): when a field on an editable block changes we
 * rebuild the block, re-emit its single line with `emitBlock`, then splice that
 * one line back into `value` at `block.line` — PRESERVING the original leading
 * indentation. We never re-emit the whole document (that would destroy the
 * bodies of compound blocks). Only the one edited line is replaced.
 *
 * Presentational + prop-driven: it owns no source state and never fetches. The
 * container passes `value` / `onChange`; `readOnly` locks both modes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { ChevronLeft, Code2, LayoutGrid, FileText, GripVertical, Plus, Trash2 } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as RadixPopover from "@radix-ui/react-popover";
import {
  SegmentedControl,
  TextInput,
  Textarea,
  Badge,
  ScrollArea,
  EmptyState,
} from "../../ui/index.js";
import {
  parseLsBlocks,
  kindToken,
  type LsBlock,
  type LsBlockKind,
} from "../../ls/parse-blocks.js";
import { emitBlock } from "../../ls/emit-block.js";
import {
  buildEpisodeStructure,
  humanizeRouteCondition,
  type ChoiceOptionStructure,
  type ChoiceResult,
  type ChoiceStructure,
  type EpisodeStructure,
  type GateRouteStructure,
  type GateStructure,
  type StateEffectStructure,
  type StateRouteImpact,
} from "../../ls/episode-structure.js";
import { useTr, type Entry } from "../../i18n/context.js";
import { INSERT_OPTIONS, MINIGAME_PRESETS, minigamePresetInsertOptions, type InsertKind } from "./block-templates.js";
import { CodeEditor, type CodeDiagnostic, type CodeSaveState } from "./CodeEditor.js";
import {
  deleteBlockAt,
  insertBlockAfter,
  insertBlockBeforeRoute,
  moveTopLevelBlock,
  topLevelIndices,
} from "./card-ops.js";
import "./EditorView.css";

/** New user-facing copy for the block editor. */
const S = {
  title: { zh: "剧本编辑器", en: "Script editor" },
  modeLabel: { zh: "编辑模式", en: "Editor mode" },
  code: { zh: "代码", en: "Code" },
  card: { zh: "卡片", en: "Cards" },
  source: { zh: "剧本源码", en: "Script source" },
  emptyTitle: { zh: "还没有内容", en: "Nothing here yet" },
  emptyDesc: {
    zh: "点击下方的“添加剧情”开始撰写，或切换到代码模式手写剧本。",
    en: "Add a story beat below to start writing, or switch to code mode.",
  },
  addBlock: { zh: "添加剧情", en: "Add story beat" },
  addBlockHere: { zh: "在此处添加剧情", en: "Insert story beat here" },
  insertBelow: { zh: "在下方添加", en: "Add below" },
  backToAddMenu: { zh: "返回添加菜单", en: "Back to add menu" },
  chooseMinigame: { zh: "选择小游戏模板", en: "Choose minigame template" },
  dragToReorder: { zh: "拖动重新排序", en: "Drag to reorder" },
  deleteBlock: { zh: "删除卡片", en: "Delete block" },
  concurrent: { zh: "同时", en: "Together" },
  editBlock: { zh: "编辑这一步", en: "Edit this step" },
  lineNo: { zh: "第 {line} 行", en: "Line {line}" },
  choiceHub: { zh: "选择分支", en: "Choice branch" },
  optionCount: { zh: "{count} 个选项", en: "{count} options" },
  gateRoutes: { zh: "结尾路线", en: "Exit routes" },
  routeCount: { zh: "{count} 条", en: "{count} routes" },
  hasFallback: { zh: "有兜底", en: "Fallback ready" },
  noFallback: { zh: "缺少兜底", en: "No fallback" },
  brave: { zh: "勇敢", en: "Brave" },
  safe: { zh: "稳妥", en: "Safe" },
  check: { zh: "检定", en: "Check" },
  effects: { zh: "影响", en: "Effect" },
  noDirectRoute: { zh: "不直接跳转", en: "No direct exit" },
  stateDrivenRoute: { zh: "通过状态影响路线", en: "State affects route" },
  stateChangeOnly: { zh: "改变状态，未匹配出口", en: "State changed; no matched exit" },
  possibleRoute: { zh: "可能影响", en: "May affect" },
  success: { zh: "成功", en: "Success" },
  fail: { zh: "失败", en: "Fail" },
  any: { zh: "任意", en: "Any" },
  routeTo: { zh: "去往", en: "Route" },
  ending: { zh: "结局", en: "Ending" },
  fallback: { zh: "兜底", en: "Fallback" },
  ifRoute: { zh: "如果", en: "If" },
  elseIfRoute: { zh: "否则若", en: "Else if" },
  elseRoute: { zh: "否则", en: "Else" },
  runDirectly: { zh: "直接执行", en: "Run directly" },
  untitledOption: { zh: "未命名选项", en: "Untitled option" },
} satisfies Record<string, Entry>;

/**
 * Field labels + option copy for the inline card editors. Kept as `Entry`
 * literals and resolved via `useTr()` so card fields switch zh/en with the rest
 * of the UI (previously these were hardcoded Chinese — the "漏翻译" gap).
 */
const F = {
  role: { zh: "角色", en: "Character" },
  pose: { zh: "姿态", en: "Pose" },
  poseOptional: { zh: "可选", en: "Optional" },
  line: { zh: "台词", en: "Line" },
  kindType: { zh: "类型", en: "Type" },
  text: { zh: "文本", en: "Text" },
  narrator: { zh: "旁白", en: "Narration" },
  inner: { zh: "内心", en: "Inner voice" },
  bg: { zh: "背景", en: "Background" },
  transition: { zh: "转场", en: "Transition" },
  transitionNone: { zh: "无", en: "None" },
  bubble: { zh: "气泡", en: "Bubble" },
  cg: { zh: "CG", en: "CG" },
  content: { zh: "内容", en: "Content" },
  music: { zh: "音乐", en: "Music" },
  play: { zh: "播放", en: "Play" },
  stop: { zh: "停止", en: "Stop" },
  track: { zh: "曲目", en: "Track" },
  sfx: { zh: "音效", en: "Sound" },
  optionId: { zh: "编号", en: "Key" },
  mode: { zh: "模式", en: "Mode" },
  brave: { zh: "勇敢", en: "Brave" },
  safe: { zh: "稳妥", en: "Safe" },
  label: { zh: "文案", en: "Label" },
  affectionChar: { zh: "角色", en: "Character" },
  delta: { zh: "增减", en: "Change" },
  signalType: { zh: "类型", en: "Type" },
  mark: { zh: "标记", en: "Flag" },
  counter: { zh: "计数", en: "Counter" },
  name: { zh: "名称", en: "Name" },
  op: { zh: "操作", en: "Op" },
  value: { zh: "数值", en: "Value" },
  trickType: { zh: "类型", en: "Type" },
  hint: { zh: "提示", en: "Prompt" },
  template: { zh: "模板", en: "Template" },
  customTemplate: { zh: "自定义", en: "Custom" },
  minigameName: { zh: "名称", en: "Name" },
  desc: { zh: "描述", en: "Description" },
  memory: { zh: "记忆", en: "Memory" },
  direction: { zh: "方向", en: "Direction" },
  from: { zh: "收到", en: "From" },
  to: { zh: "发出", en: "To" },
  achId: { zh: "成就 ID", en: "Achievement ID" },
  title: { zh: "标题", en: "Title" },
  branch: { zh: "分支", en: "Branch" },
  paramKey: { zh: "字段", en: "Field" },
  paramValue: { zh: "值", en: "Value" },
  condition: { zh: "条件", en: "Condition" },
  routeTo: { zh: "去往", en: "Route to" },
  endType: { zh: "结束", en: "End" },
} satisfies Record<string, Entry>;

/**
 * Bilingual block-kind badge labels. `parse-blocks.kindLabel` is zh-only (it is
 * pure + shared with tests); the editor badge renders through `useTr()` so the
 * kind tag switches with the rest of the UI (previously it stayed Chinese in EN).
 */
const KIND_TR: Record<LsBlockKind, Entry> = {
  episode: { zh: "集", en: "Episode" },
  bg: { zh: "背景", en: "Background" },
  char: { zh: "立绘", en: "Character" },
  dialogue: { zh: "对白", en: "Dialogue" },
  narration: { zh: "旁白", en: "Narration" },
  cg: { zh: "CG", en: "CG" },
  music: { zh: "音乐", en: "Music" },
  sfx: { zh: "音效", en: "SFX" },
  pause: { zh: "停顿", en: "Pause" },
  choice: { zh: "选择", en: "Choice" },
  option: { zh: "选项", en: "Option" },
  check: { zh: "检定", en: "Check" },
  if: { zh: "条件", en: "Condition" },
  gate: { zh: "路线", en: "Route" },
  affection: { zh: "好感", en: "Affection" },
  signal: { zh: "信号", en: "Signal" },
  trick: { zh: "体感", en: "Trick" },
  phone: { zh: "手机", en: "Phone" },
  phoneText: { zh: "短信", en: "Message" },
  minigame: { zh: "小游戏", en: "Minigame" },
  achievement: { zh: "成就", en: "Achievement" },
  butterfly: { zh: "蝴蝶", en: "Butterfly" },
  param: { zh: "参数", en: "Field" },
  unknown: { zh: "未知", en: "Unknown" },
};

export interface EditorViewProps {
  /** The raw `.ls` source — the single source of truth. */
  readonly value: string;
  /** Called with the next source whenever an edit is made (code or card). */
  readonly onChange: (value: string) => void;
  /** When true both modes are locked (no field is editable). */
  readonly readOnly?: boolean;
  /** Highlight and scroll to a 1-based source line in card mode. */
  readonly selectedLine?: number | null;
  /** Optional controlled Cards/Code sub-mode (App uses this for Agent context). */
  readonly mode?: EditorMode;
  readonly onModeChange?: (mode: EditorMode) => void;
  /** One-based source line to reveal when entering Code from Studio/Canvas. */
  readonly initialLine?: number | null;
  readonly onInitialLineApplied?: () => void;
  readonly diagnostics?: readonly CodeDiagnostic[];
  readonly saveState?: CodeSaveState;
}

export type EditorMode = "code" | "card";

export function EditorView(props: EditorViewProps) {
  return <EditorViewForTest {...props} />;
}

/**
 * The editor implementation. Identical to `EditorView` but accepts an
 * `initialMode` seam so tests can render either mode directly (the node test
 * environment has no click harness to flip the internal SegmentedControl).
 * Not part of the public API — prefer `EditorView`.
 *
 * Card is the DEFAULT view (Notion-style block canvas): each parsed beat becomes
 * a draggable card with a hover gutter (drag grip + delete) and hover "+" aff:
 * insertion/reorder/delete all rewrite the source string and call `onChange`.
 */
export function EditorViewForTest({
  value,
  onChange,
  readOnly,
  selectedLine,
  mode: controlledMode,
  onModeChange,
  initialLine,
  onInitialLineApplied,
  diagnostics,
  saveState,
  initialMode = "card",
}: EditorViewProps & { initialMode?: EditorMode }) {
  const tr = useTr();
  const [localMode, setLocalMode] = useState<EditorMode>(initialMode);
  const mode = controlledMode ?? localMode;
  const setMode = useCallback((next: EditorMode) => {
    if (controlledMode === undefined) setLocalMode(next);
    onModeChange?.(next);
  }, [controlledMode, onModeChange]);

  const modeOptions = useMemo(
    () => [
      { value: "card" as const, label: tr(S.card), icon: <LayoutGrid size={14} aria-hidden /> },
      { value: "code" as const, label: tr(S.code), icon: <Code2 size={14} aria-hidden /> },
    ],
    [tr],
  );

  // Only build the rich episode model when card mode is showing — code mode
  // never needs parsed blocks. The model keeps card/canvas route attribution in
  // sync, so @choice and @gate explain the same story logic.
  const structure = useMemo(
    () => (mode === "card" ? buildEpisodeStructure(value) : undefined),
    [mode, value],
  );
  const blocks = structure?.blocks ?? [];
  const insertAtSafeTail = useCallback(
    (kind: InsertKind) => onChange(insertBlockBeforeRoute(value, blocks, kind)),
    [blocks, onChange, value],
  );

  return (
    <div className="lc-editor">
      <header className="lc-editor__bar">
        <span className="lc-editor__title">{tr(S.title)}</span>
        <div className="lc-editor__bar-actions">
          {mode === "card" && !readOnly ? (
            <AddBlockMenu label={tr(S.addBlock)} variant="button" onPick={insertAtSafeTail} />
          ) : null}
          <SegmentedControl<EditorMode>
            aria-label={tr(S.modeLabel)}
            options={modeOptions}
            value={mode}
            onValueChange={setMode}
          />
        </div>
      </header>

      <div className="lc-editor__stage">
        {mode === "code" ? (
          <CodeEditor
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            initialLine={initialLine}
            onInitialLineApplied={onInitialLineApplied}
            diagnostics={diagnostics}
            saveState={saveState}
            onBackToCards={() => setMode("card")}
          />
        ) : (
          <CardCanvas
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            blocks={blocks}
            structure={structure}
            selectedLine={selectedLine}
          />
        )}
      </div>
    </div>
  );
}

// ── card canvas: the Notion-style block view ─────────────────────────────────

interface CardCanvasProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly readOnly?: boolean;
  readonly blocks: readonly LsBlock[];
  readonly structure?: EpisodeStructure;
  readonly selectedLine?: number | null;
}

/**
 * The card canvas. Wraps top-level (depth-0) cards in a dnd-kit sortable so they
 * can be dragged to reorder; drops MOVE the dragged block's whole line-range and
 * call `onChange`. A hover "+" between/after cards opens the add-block menu; a
 * per-card delete removes its line-range. Editing a field still patches only
 * that one line via `patchBlock` (unchanged round-trip contract).
 */
function CardCanvas({ value, onChange, readOnly, blocks, structure, selectedLine }: CardCanvasProps) {
  const tr = useTr();
  const [localSelectedLine, setLocalSelectedLine] = useState<number | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const activeLine = selectedLine ?? localSelectedLine;

  // Drag reorder is restricted to top-level (depth-0) siblings — nested cards
  // are moved in code mode (see card-ops.ts limitation note). Sortable ids are
  // the block's array index (stable within one parse of `value`).
  const concurrentAttachments = useMemo(() => {
    const hidden = new Set<number>();
    const attached = new Map<number, LsBlock[]>();
    let anchor: LsBlock | undefined;
    for (const block of blocks) {
      if (block.concurrent && anchor) {
        hidden.add(block.line);
        const list = attached.get(anchor.line) ?? [];
        list.push(block);
        attached.set(anchor.line, list);
        continue;
      }
      anchor = block;
    }
    return { hidden, attached };
  }, [activeLine, blocks]);
  const hiddenCardLines = useMemo(() => {
    const hidden = new Set<number>(concurrentAttachments.hidden);
    const gate = structure?.gate;
    if (gate) {
      for (const block of blocks) {
        if (block.line !== activeLine && block.line > gate.range.start && block.line < gate.range.end) hidden.add(block.line);
      }
    }
    return hidden;
  }, [activeLine, blocks, concurrentAttachments.hidden, structure?.gate]);
  const visibleRows = useMemo(
    () => blocks.map((block, index) => ({ block, index })).filter(({ block }) => !hiddenCardLines.has(block.line)),
    [blocks, hiddenCardLines],
  );
  const topIndices = useMemo(
    () => topLevelIndices(blocks).filter((i) => {
      const block = blocks[i];
      return block ? !hiddenCardLines.has(block.line) : false;
    }),
    [blocks, hiddenCardLines],
  );
  const sortableIds = useMemo(() => topIndices.map((i) => `card-${i}`), [topIndices]);
  const structureByLine = useMemo(() => {
    const choices = new Map<number, ChoiceStructure>();
    const options = new Map<number, ChoiceOptionStructure>();
    for (const choice of structure?.choices ?? []) {
      choices.set(choice.line, choice);
      for (const option of choice.options) {
        options.set(option.line, option);
      }
    }
    return {
      choices,
      options,
      gate: structure?.gate,
      allChoices: structure?.choices ?? [],
    };
  }, [structure]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const from = Number(String(active.id).replace("card-", ""));
      const to = Number(String(over.id).replace("card-", ""));
      if (Number.isNaN(from) || Number.isNaN(to)) return;
      onChange(moveTopLevelBlock(value, blocks, from, to));
    },
    [blocks, onChange, value],
  );

  const insertAfter = useCallback(
    (afterIndex: number, kind: InsertKind) => {
      onChange(insertBlockAfter(value, blocks, afterIndex, kind));
    },
    [blocks, onChange, value],
  );

  const removeAt = useCallback(
    (index: number) => onChange(deleteBlockAt(value, blocks, index)),
    [blocks, onChange, value],
  );

  if (blocks.length === 0) {
    return (
      <div className="lc-editor__empty-wrap">
        <EmptyState
          className="lc-editor__empty"
          icon={<FileText size={22} aria-hidden />}
          title={tr(S.emptyTitle)}
          description={tr(S.emptyDesc)}
        />
        {readOnly ? null : (
          <AddBlockMenu
            label={tr(S.addBlock)}
            variant="button"
            onPick={(kind) => insertAfter(-1, kind)}
          />
        )}
      </div>
    );
  }

  return (
    <ScrollArea className="lc-editor__cards" viewportClassName="lc-editor__cards-vp">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <ol className="lc-editor__list">
            {visibleRows.map(({ block, index: i }) => {
              const attachedConcurrent = concurrentAttachments.attached.get(block.line) ?? [];
              const attachedSelected = attachedConcurrent.some((item) => item.line === activeLine);
              return (
                <BlockRow
                  key={`${block.line}-${i}`}
                  block={block}
                  index={i}
                  sortableId={block.depth === 0 ? `card-${i}` : undefined}
                  attachedConcurrent={attachedConcurrent}
                  activeLine={activeLine}
                  readOnly={readOnly}
                  onPatch={(next) => onChange(patchBlock(value, block, next))}
                  onPatchAttached={(item, next) => onChange(patchBlock(value, item, next))}
                  onInsertAfter={(kind) => insertAfter(i, kind)}
                  onDelete={() => removeAt(i)}
                  selected={activeLine === block.line || attachedSelected}
                  expanded={activeLine === block.line}
                  choice={structureByLine.choices.get(block.line)}
                  option={structureByLine.options.get(block.line)}
                  gate={structureByLine.gate?.line === block.line ? structureByLine.gate : undefined}
                  allChoices={structureByLine.allChoices}
                  onSelect={() => setLocalSelectedLine((line) => (line === block.line ? null : block.line))}
                  onSelectAttached={(line) => setLocalSelectedLine((current) => (current === line ? null : line))}
                />
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>
    </ScrollArea>
  );
}

// ── add-block menu ───────────────────────────────────────────────────────────

interface AddBlockMenuProps {
  readonly onPick: (kind: InsertKind) => void;
  readonly label: string;
  /** "gutter" = a subtle hover "+" between cards; "button" = a labelled button. */
  readonly variant: "gutter" | "button";
}

/**
 * The Radix Popover add-block menu. Lists every insertable kind with a
 * token-tinted swatch; selecting one inserts its template and closes the menu.
 */
export function AddBlockMenu({ onPick, label, variant }: AddBlockMenuProps) {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<"root" | "minigame">("root");
  const options = useMemo(() => INSERT_OPTIONS, []);
  const minigameOptions = useMemo(() => minigamePresetInsertOptions(), []);

  const pick = (kind: InsertKind) => {
    onPick(kind);
    setOpen(false);
    setPanel("root");
  };

  useEffect(() => {
    if (!open) setPanel("root");
  }, [open]);

  const trigger =
    variant === "button" ? (
      <button type="button" className="lc-addblock__cta" aria-label={label}>
        <Plus size={14} aria-hidden />
        <span>{label}</span>
      </button>
    ) : (
      <button type="button" className="lc-addblock__plus" aria-label={label} title={label}>
        <Plus size={14} aria-hidden />
      </button>
    );

  return (
    <RadixPopover.Root open={open} onOpenChange={setOpen}>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          className="lc-pop lc-addblock__menu"
          side="bottom"
          align="start"
          sideOffset={6}
        >
          {panel === "minigame" ? (
            <>
              <button type="button" className="lc-addblock__back" onClick={() => setPanel("root")}>
                <ChevronLeft size={14} aria-hidden />
                {tr(S.backToAddMenu)}
              </button>
              <ul className="lc-addblock__list" role="menu" aria-label={tr(S.chooseMinigame)}>
                {minigameOptions.map((opt) => (
                  <li key={opt.kind} role="none">
                    <button type="button" role="menuitem" className="lc-addblock__item" onClick={() => pick(opt.kind)}>
                      <span
                        className="lc-addblock__swatch"
                        aria-hidden
                        style={{ ["--lc-kind" as string]: `var(${opt.token})` }}
                      />
                      <span className="lc-addblock__item-label">{tr(opt.label)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <ul className="lc-addblock__list" role="menu" aria-label={label}>
              {options.map((opt) => (
                <li key={opt.kind} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className="lc-addblock__item"
                    onClick={() => {
                      if (opt.kind === "minigame") setPanel("minigame");
                      else pick(opt.kind);
                    }}
                  >
                    <span
                      className="lc-addblock__swatch"
                      aria-hidden
                      style={{ ["--lc-kind" as string]: `var(${opt.token})` }}
                    />
                    <span className="lc-addblock__item-label">{tr(opt.label)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

// ── source splicing ─────────────────────────────────────────────────────────

/**
 * Replace a single 1-based line in `source` with `newLine`, PRESERVING that
 * line's original leading indentation. Out-of-range line numbers are a no-op
 * (returns the source untouched) so a stale block never corrupts the document.
 */
export function replaceSourceLine(source: string, lineNo: number, newLine: string): string {
  const lines = source.split(/\r?\n/);
  const idx = lineNo - 1;
  if (idx < 0 || idx >= lines.length) return source;
  const original = lines[idx] ?? "";
  const indent = original.match(/^\s*/)?.[0] ?? "";
  lines[idx] = indent + newLine;
  return lines.join("\n");
}

/**
 * Build an updated block from a field patch, re-emit its single line, and splice
 * it back into the source at the block's line. Only the one line changes.
 * Exported so the round-trip contract (卡片 edit → patched source) is unit-testable
 * without a DOM (the node test environment has no interaction harness).
 */
export function patchBlock(source: string, block: LsBlock, patch: Partial<LsBlock>): string {
  // Merge the patch onto the block and re-emit. The cast is safe: a patch only
  // ever carries fields that belong to the block's own kind (the row editors
  // are kind-specific), so the merged object stays a valid discriminated member.
  const updated = { ...block, ...patch } as LsBlock;
  const line = emitBlock(updated);
  const next = replaceSourceLine(source, block.line, line);
  const patchedId = (patch as { readonly id?: unknown }).id;
  if (
    block.kind === "option" &&
    typeof patchedId === "string" &&
    block.id &&
    patchedId &&
    patchedId !== block.id
  ) {
    return renameChoiceHistoryReferences(next, block.id, patchedId);
  }
  return next;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renameChoiceHistoryReferences(source: string, from: string, to: string): string {
  const ref = new RegExp(`\\b${escapeRegExp(from)}\\.(success|fail|any)\\b`, "g");
  return source.replace(ref, `${to}.$1`);
}

// ── block rows ───────────────────────────────────────────────────────────────

interface BlockBodyProps {
  readonly block: LsBlock;
  readonly readOnly?: boolean;
  readonly choice?: ChoiceStructure;
  readonly option?: ChoiceOptionStructure;
  readonly gate?: GateStructure;
  readonly allChoices?: readonly ChoiceStructure[];
  /** Apply a field patch to this block (rebuild + splice back into source). */
  readonly onPatch: (patch: Partial<LsBlock>) => void;
}

interface BlockRowProps extends BlockBodyProps {
  readonly index: number;
  /** dnd-kit sortable id — present only for top-level (depth-0) cards. */
  readonly sortableId?: string;
  readonly attachedConcurrent?: readonly LsBlock[];
  readonly activeLine?: number | null;
  readonly onPatchAttached: (block: LsBlock, patch: Partial<LsBlock>) => void;
  readonly onInsertAfter: (kind: InsertKind) => void;
  readonly onDelete: () => void;
  readonly selected?: boolean;
  readonly expanded?: boolean;
  readonly choice?: ChoiceStructure;
  readonly option?: ChoiceOptionStructure;
  readonly gate?: GateStructure;
  readonly allChoices?: readonly ChoiceStructure[];
  readonly onSelect: () => void;
  readonly onSelectAttached: (line: number) => void;
}

/**
 * A single beat row — indented by depth, tinted by kind, editable when a leaf.
 * Top-level rows are dnd-kit sortable (drag via the gutter grip). A hover gutter
 * exposes the grip + add "+"; a hover action exposes delete. A trailing "+"
 * affordance below the row inserts a block after it.
 */
function BlockRow({
  block,
  readOnly,
  onPatch,
  sortableId,
  attachedConcurrent = [],
  activeLine,
  onInsertAfter,
  onDelete,
  onPatchAttached,
  selected,
  expanded,
  choice,
  option,
  gate,
  allChoices = [],
  onSelect,
  onSelectAttached,
}: BlockRowProps) {
  const tr = useTr();
  const tone = `var(${kindToken(block.kind)})`;
  const draggable = Boolean(sortableId) && !readOnly;
  const summary = describeStructuredBlock(block, tr, choice, option, gate);

  // useSortable is a hook — always called. When this row is not top-level we
  // still call it with a synthetic id but never wire its listeners, so the row
  // is inert to dragging (nested cards reorder in code mode).
  const sortable = useSortable({ id: sortableId ?? `inert-${block.line}`, disabled: !draggable });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const rowRef = useRef<HTMLLIElement | null>(null);

  const setCombinedRef = useCallback(
    (node: HTMLLIElement | null) => {
      rowRef.current = node;
      setNodeRef(node);
    },
    [setNodeRef],
  );

  useEffect(() => {
    if (!selected) return;
    rowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selected]);

  const style: CSSProperties = {
    ["--lc-kind" as string]: tone,
    ["--lc-depth" as string]: block.depth,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.45 } : {}),
  };

  return (
    <li
      className="lc-block"
      ref={setCombinedRef}
      style={style}
      data-line={block.line}
      data-selected={selected || undefined}
      data-expanded={expanded || undefined}
      data-dragging={isDragging || undefined}
    >
      {readOnly ? (
        <span className="lc-block__rail" aria-hidden />
      ) : (
        <div className="lc-block__gutter" aria-hidden={undefined}>
          <button
            type="button"
            className="lc-block__grip"
            aria-label={tr(S.dragToReorder)}
            title={tr(S.dragToReorder)}
            disabled={!draggable}
            {...(draggable ? attributes : {})}
            {...(draggable ? listeners : {})}
          >
            <GripVertical size={14} aria-hidden />
          </button>
          <AddBlockMenu
            variant="gutter"
            label={tr(S.insertBelow)}
            onPick={(kind) => onInsertAfter(kind)}
          />
        </div>
      )}
      <span className="lc-block__rail" aria-hidden />
      <div className="lc-block__main">
        <button
          type="button"
          className="lc-block__summary"
          aria-label={`${tr(S.editBlock)} · ${summary}`}
          aria-expanded={expanded ? "true" : "false"}
          onClick={onSelect}
        >
          <span className="lc-block__kind">
            <span className="lc-block__kind-dot" aria-hidden />
            {tr(KIND_TR[block.kind])}
          </span>
          {block.concurrent ? <span className="lc-block__concurrent">{tr(S.concurrent)}</span> : null}
          <span className="lc-block__summary-text">{summary}</span>
          <span className="lc-block__line">{tr(S.lineNo, { line: block.line })}</span>
        </button>
        {attachedConcurrent.length > 0 ? (
          <div className="lc-block__attached-concurrent" role="group" aria-label={tr(S.concurrent)}>
            <span className="lc-block__attached-concurrent-label">{tr(S.concurrent)}</span>
            <div className="lc-block__attached-concurrent-items">
              {attachedConcurrent.map((item) => (
                <AttachedConcurrentItem
                  key={`${item.line}:${item.raw}`}
                  item={item}
                  active={activeLine === item.line}
                  readOnly={readOnly}
                  onSelect={() => onSelectAttached(item.line)}
                  onPatch={(patch) => onPatchAttached(item, patch)}
                />
              ))}
            </div>
          </div>
        ) : null}
        {expanded ? null : <StructuredBlockPreview choice={choice} option={option} gate={gate} allChoices={allChoices} />}
        {expanded ? (
          <div className="lc-block__editor">
            <BlockBody
              block={block}
              readOnly={readOnly}
              choice={choice}
              option={option}
              gate={gate}
              allChoices={allChoices}
              onPatch={onPatch}
            />
          </div>
        ) : null}
      </div>
      {readOnly ? null : (
        <div className="lc-block__actions">
          <button
            type="button"
            className="lc-block__delete"
            aria-label={tr(S.deleteBlock)}
            title={tr(S.deleteBlock)}
            onClick={onDelete}
          >
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      )}
    </li>
  );
}

function AttachedConcurrentItem({
  item,
  active,
  readOnly,
  onSelect,
  onPatch,
}: {
  readonly item: LsBlock;
  readonly active: boolean;
  readonly readOnly?: boolean;
  readonly onSelect: () => void;
  readonly onPatch: (patch: Partial<LsBlock>) => void;
}) {
  const tr = useTr();
  return (
    <div className="lc-block__attached-concurrent-entry" style={{ ["--lc-kind" as string]: `var(${kindToken(item.kind)})` }}>
      <button
        type="button"
        className="lc-block__attached-concurrent-item"
        data-active={active || undefined}
        aria-expanded={active ? "true" : "false"}
        aria-label={`${tr(S.editBlock)} · ${describeBlock(item)}`}
        onClick={onSelect}
      >
        <span className="lc-block__attached-concurrent-kind">{tr(KIND_TR[item.kind])}</span>
        <span className="lc-block__attached-concurrent-text">{describeBlock(item)}</span>
      </button>
      {active ? (
        <div className="lc-block__attached-concurrent-editor">
          <BlockBody block={item} readOnly={readOnly} onPatch={onPatch} />
        </div>
      ) : null}
    </div>
  );
}

function describeStructuredBlock(
  block: LsBlock,
  tr: ReturnType<typeof useTr>,
  choice?: ChoiceStructure,
  option?: ChoiceOptionStructure,
  gate?: GateStructure,
): string {
  if (choice) {
    return `${tr(S.choiceHub)} · ${tr(S.optionCount, { count: choice.options.length })}`;
  }
  if (gate) {
    return [
      tr(S.gateRoutes),
      tr(S.routeCount, { count: gate.routes.length }),
      gate.hasFallback ? tr(S.hasFallback) : tr(S.noFallback),
    ].join(" · ");
  }
  if (option) {
    const impacts = option.routeImpacts.map((impact) => routeImpactLabel(impact.result, impact.route, tr, option));
    return [
      option.id,
      optionModeLabel(option.mode, tr),
      option.text ? `「${option.text}」` : null,
      impacts[0],
    ].filter(Boolean).join(" · ") || describeBlock(block);
  }
  return describeBlock(block);
}

function StructuredBlockPreview({
  choice,
  option,
  gate,
  allChoices = [],
}: {
  readonly choice?: ChoiceStructure;
  readonly option?: ChoiceOptionStructure;
  readonly gate?: GateStructure;
  readonly allChoices?: readonly ChoiceStructure[];
}) {
  if (choice) return <ChoiceCardPreview choice={choice} />;
  if (gate) return <GateCardPreview gate={gate} choices={allChoices} />;
  if (option) return <OptionCardPreview option={option} compact />;
  return null;
}

function ChoiceCardPreview({ choice }: { readonly choice: ChoiceStructure }) {
  return (
    <div className="lc-block__preview lc-choice-card" aria-label="choice route impact">
      {choice.options.map((option) => (
        <OptionCardPreview key={`${option.line}-${option.id ?? "option"}`} option={option} />
      ))}
    </div>
  );
}

function OptionCardPreview({
  option,
  compact,
}: {
  readonly option: ChoiceOptionStructure;
  readonly compact?: boolean;
}) {
  const tr = useTr();
  return (
    <div className={compact ? "lc-option-card lc-option-card--compact" : "lc-option-card"}>
      <div className="lc-option-card__head">
        <span className="lc-option-card__id">{option.id ?? "?"}</span>
        <span className="lc-option-card__label">{option.text || tr(S.untitledOption)}</span>
        {option.mode ? (
          <span className={`lc-option-card__pill lc-option-card__pill--${option.mode}`}>
            {optionModeLabel(option.mode, tr)}
          </span>
        ) : null}
        {option.check ? (
          <span className="lc-option-card__pill">
            {tr(S.check)}
            {option.check.attr ? ` ${option.check.attr}` : ""}
            {option.check.dc ? ` ≥ ${option.check.dc}` : ""}
          </span>
        ) : null}
      </div>
      <div className="lc-option-card__meta">
        {option.effects.map((effect) => (
          <span key={`${effect.line}-${effect.label}`} className="lc-option-card__chip">
            {effectDisplayLabel(effect, tr)}
          </span>
        ))}
        {option.routeImpacts.length > 0 || option.stateRouteImpacts.length > 0 ? (
          <>
            {option.routeImpacts.map((impact) => (
            <span
              key={`${impact.route.line}-${impact.result}-${impact.route.outcome.value}`}
              className="lc-option-card__route"
            >
              {routeImpactLabel(impact.result, impact.route, tr, option)}
            </span>
            ))}
            {option.stateRouteImpacts.map((impact) => (
              <span
                key={`${impact.effect.line}-${impact.key}-${impact.route.index}`}
                className="lc-option-card__route"
              >
                {stateRouteImpactLabel(impact, tr, option)}
              </span>
            ))}
          </>
        ) : (
          <span className="lc-option-card__muted">
            {option.effects.length > 0 ? tr(S.stateChangeOnly) : tr(S.noDirectRoute)}
          </span>
        )}
      </div>
    </div>
  );
}

function GateCardPreview({
  gate,
  choices = [],
}: {
  readonly gate: GateStructure;
  readonly choices?: readonly ChoiceStructure[];
}) {
  const tr = useTr();
  return (
    <div className="lc-block__preview lc-gate-card" aria-label="gate route order">
      {gate.routes.map((route) => {
        const logic = route.isFallback ? tr(S.elseRoute) : route.index === 0 ? tr(S.ifRoute) : tr(S.elseIfRoute);
        const condition = route.isFallback
          ? tr(S.fallback)
          : humanizeRouteCondition(route, choices) || route.condition || tr(S.runDirectly);
        return (
          <div key={`${route.line}-${route.index}`} className="lc-gate-card__row" data-fallback={route.isFallback || undefined}>
            <span className="lc-gate-card__index">{String(route.index + 1).padStart(2, "0")}</span>
            <span className="lc-gate-card__logic">{logic}</span>
            <span className={route.isFallback ? "lc-gate-card__condition lc-gate-card__condition--fallback" : "lc-gate-card__condition"}>
              {condition}
            </span>
            <span className="lc-gate-card__arrow" aria-hidden="true">→</span>
            <span className="lc-gate-card__outcome">{outcomeLabel(route, tr)}</span>
          </div>
        );
      })}
    </div>
  );
}

function optionModeLabel(mode: ChoiceOptionStructure["mode"], tr: ReturnType<typeof useTr>): string | null {
  if (mode === "brave") return tr(S.brave);
  if (mode === "safe") return tr(S.safe);
  return null;
}

function resultLabel(result: ChoiceResult, tr: ReturnType<typeof useTr>): string {
  if (result === "success") return tr(S.success);
  if (result === "fail") return tr(S.fail);
  return tr(S.any);
}

function effectDisplayLabel(effect: StateEffectStructure, tr: ReturnType<typeof useTr>): string {
  if (effect.scope === "success") return `${tr(S.success)}${tr(S.effects)} ${effect.label}`;
  if (effect.scope === "fail") return `${tr(S.fail)}${tr(S.effects)} ${effect.label}`;
  return `${tr(S.effects)} ${effect.label}`;
}

function outcomeLabel(route: GateRouteStructure, tr: ReturnType<typeof useTr>): string {
  return route.outcome.kind === "next"
    ? `${tr(S.routeTo)} ${route.outcome.value}`
    : `${tr(S.ending)} · ${route.outcome.value}`;
}

function routeImpactLabel(
  result: ChoiceResult,
  route: GateRouteStructure,
  tr: ReturnType<typeof useTr>,
  option?: ChoiceOptionStructure,
): string {
  const expectedCondition = option?.id ? `${option.id}.${result}` : undefined;
  const condition = route.condition?.trim();
  const readableCondition = humanizeRouteCondition(
    route,
    option ? [{ line: 0, range: { start: 0, end: 0 }, options: [option] }] : [],
  );
  const prefix =
    condition && expectedCondition && condition !== expectedCondition
      ? `${resultLabel(result, tr)} · ${readableCondition ?? condition}`
      : resultLabel(result, tr);
  return `${prefix} → ${outcomeLabel(route, tr).replace(`${tr(S.routeTo)} `, "")}`;
}

function stateRouteImpactLabel(
  impact: StateRouteImpact,
  tr: ReturnType<typeof useTr>,
  option?: ChoiceOptionStructure,
): string {
  const condition =
    humanizeRouteCondition(
      impact.route,
      option ? [{ line: 0, range: { start: 0, end: 0 }, options: [option] }] : [],
    ) ?? impact.route.condition ?? impact.key;
  return `${tr(S.possibleRoute)} ${condition} ${outcomeLabel(impact.route, tr).replace(`${tr(S.routeTo)} `, "→ ")}`;
}

/** Enum option lists reused by the inline selects. */
const TRANSITIONS = ["dissolve", "fade", "cut", "slow"] as const;
const SIGNAL_OPS = ["=", "+", "-"] as const;

/** Localized read-only summaries for the compound kinds shown without editors. */
const SUMMARY: Partial<Record<string, Entry>> = {
  choice: { zh: "玩家选择", en: "Player choice" },
  phone: { zh: "手机消息", en: "Phone messages" },
  check: { zh: "D20 检定", en: "D20 check" },
  pause: { zh: "等待玩家点击继续", en: "Wait for the next tap" },
};

/** ending types for the `@end <type>` route select (LS-SPEC §4.1). */
const END_TYPES = ["complete", "to_be_continued", "bad_ending"] as const;

/**
 * Render a block's editable body. Editable kinds get inline field editors (all
 * labels routed through `useTr()` for zh/en parity); the few genuinely
 * multi-line-owning compound openers (choice / phone / check-opener /
 * gate-opener / unknown) show a read-only summary. Tolerant of missing fields.
 */
function BlockBody({ block, readOnly, choice, option, gate, allChoices = [], onPatch }: BlockBodyProps) {
  const tr = useTr();
  const disabled = Boolean(readOnly);

  switch (block.kind) {
    case "dialogue":
      return (
        <FieldGrid>
          <Field label={tr(F.role)}>
            <BareInput
              aria-label={tr(F.role)}
              value={block.speaker}
              disabled={disabled}
              onCommit={(v) => onPatch({ speaker: v.toUpperCase() })}
            />
          </Field>
          <Field label={tr(F.pose)}>
            <BareInput
              aria-label={tr(F.pose)}
              value={block.pose ?? ""}
              placeholder={tr(F.poseOptional)}
              disabled={disabled}
              onCommit={(v) => onPatch({ pose: v.trim() || undefined })}
            />
          </Field>
          <Field label={tr(F.line)} wide>
            <BareTextarea
              aria-label={tr(F.line)}
              value={block.text}
              disabled={disabled}
              onCommit={(v) => onPatch({ text: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "narration":
      return (
        <FieldGrid>
          <Field label={tr(F.kindType)}>
            <Select
              aria-label={tr(F.kindType)}
              value={block.speaker}
              options={[
                { value: "NARRATOR", label: tr(F.narrator) },
                { value: "YOU", label: tr(F.inner) },
              ]}
              disabled={disabled}
              onSelect={(v) => onPatch({ speaker: v as "NARRATOR" | "YOU" })}
            />
          </Field>
          <Field label={tr(F.text)} wide>
            <BareTextarea
              aria-label={tr(F.text)}
              value={block.text}
              disabled={disabled}
              onCommit={(v) => onPatch({ text: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "bg":
      return (
        <FieldGrid>
          <Field label={tr(F.bg)}>
            <BareInput
              aria-label={tr(F.bg)}
              value={block.name ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ name: v.trim() || undefined })}
            />
          </Field>
          <Field label={tr(F.transition)}>
            <TransitionSelect
              value={block.transition}
              disabled={disabled}
              onSelect={(v) => onPatch({ transition: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "char":
      return (
        <FieldGrid>
          <Field label={tr(F.role)}>
            <BareInput
              aria-label={tr(F.role)}
              value={block.character}
              disabled={disabled}
              onCommit={(v) => onPatch({ character: v.trim() || block.character })}
            />
          </Field>
          <Field label={block.bubble ? tr(F.bubble) : tr(F.pose)}>
            <BareInput
              aria-label={block.bubble ? tr(F.bubble) : tr(F.pose)}
              value={block.pose ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ pose: v.trim() || undefined })}
            />
          </Field>
          {block.bubble ? null : (
            <Field label={tr(F.transition)}>
              <TransitionSelect
                value={block.transition}
                disabled={disabled}
                onSelect={(v) => onPatch({ transition: v })}
              />
            </Field>
          )}
        </FieldGrid>
      );

    case "cg":
      return (
        <FieldGrid>
          <Field label={tr(F.cg)}>
            <BareInput
              aria-label={tr(F.cg)}
              value={block.name ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ name: v.trim() || undefined })}
            />
          </Field>
          <Field label={tr(F.content)} wide>
            <BareInput
              aria-label={tr(F.content)}
              value={block.content ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ content: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "music":
      return (
        <FieldGrid>
          <Field label={tr(F.music)}>
            <Select
              aria-label={tr(F.music)}
              value={block.stop ? "stop" : "play"}
              options={[
                { value: "play", label: tr(F.play) },
                { value: "stop", label: tr(F.stop) },
              ]}
              disabled={disabled}
              onSelect={(v) =>
                v === "stop"
                  ? onPatch({ stop: true, name: undefined })
                  : onPatch({ stop: undefined })
              }
            />
          </Field>
          {block.stop ? null : (
            <Field label={tr(F.track)} wide>
              <BareInput
                aria-label={tr(F.track)}
                value={block.name ?? ""}
                disabled={disabled}
                onCommit={(v) => onPatch({ name: v.trim() || undefined })}
              />
            </Field>
          )}
        </FieldGrid>
      );

    case "sfx":
      return (
        <FieldGrid>
          <Field label={tr(F.sfx)} wide>
            <BareInput
              aria-label={tr(F.sfx)}
              value={block.name ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ name: v.trim() || undefined })}
            />
          </Field>
        </FieldGrid>
      );

    case "option":
      return (
        <>
          {option ? <OptionCardPreview option={option} compact /> : null}
          <FieldGrid>
            <Field label={tr(F.optionId)}>
              <BareInput
                aria-label={tr(F.optionId)}
                value={block.id ?? ""}
                disabled={disabled}
                onCommit={(v) => onPatch({ id: v.trim() || undefined })}
              />
            </Field>
            <Field label={tr(F.mode)}>
              <Select
                aria-label={tr(F.mode)}
                value={block.mode ?? ""}
                options={[
                  { value: "brave", label: tr(F.brave) },
                  { value: "safe", label: tr(F.safe) },
                ]}
                disabled={disabled}
                onSelect={(v) => onPatch({ mode: v as "brave" | "safe" })}
              />
            </Field>
            <Field label={tr(F.label)} wide>
              <BareInput
                aria-label={tr(F.label)}
                value={block.text ?? ""}
                disabled={disabled}
                onCommit={(v) => onPatch({ text: v })}
              />
            </Field>
          </FieldGrid>
        </>
      );

    case "affection":
      return (
        <FieldGrid>
          <Field label={tr(F.affectionChar)}>
            <BareInput
              aria-label={tr(F.affectionChar)}
              value={block.character ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ character: v.trim() || undefined })}
            />
          </Field>
          <Field label={tr(F.delta)}>
            <BareInput
              aria-label={tr(F.delta)}
              value={block.delta ?? ""}
              placeholder="+1 / -2"
              disabled={disabled}
              onCommit={(v) => onPatch({ delta: v.trim() || undefined })}
            />
          </Field>
        </FieldGrid>
      );

    case "signal":
      return (
        <FieldGrid>
          <Field label={tr(F.signalType)}>
            <Select
              aria-label={tr(F.signalType)}
              value={block.signalKind ?? "mark"}
              options={[
                { value: "mark", label: tr(F.mark) },
                { value: "int", label: tr(F.counter) },
              ]}
              disabled={disabled}
              onSelect={(v) =>
                v === "int"
                  ? onPatch({ signalKind: "int", op: block.op ?? "=", value: block.value ?? "0" })
                  : onPatch({ signalKind: "mark", op: undefined, value: undefined })
              }
            />
          </Field>
          <Field label={tr(F.name)}>
            <BareInput
              aria-label={tr(F.name)}
              value={block.name ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ name: v.trim() || undefined })}
            />
          </Field>
          {block.signalKind === "int" ? (
            <>
              <Field label={tr(F.op)}>
                <Select
                  aria-label={tr(F.op)}
                  value={block.op ?? "="}
                  options={SIGNAL_OPS.map((op) => ({ value: op, label: op }))}
                  disabled={disabled}
                  onSelect={(v) => onPatch({ op: v })}
                />
              </Field>
              <Field label={tr(F.value)}>
                <BareInput
                  aria-label={tr(F.value)}
                  value={block.value ?? ""}
                  disabled={disabled}
                  onCommit={(v) => onPatch({ value: v.trim() || undefined })}
                />
              </Field>
            </>
          ) : null}
        </FieldGrid>
      );

    case "trick":
      return (
        <FieldGrid>
          <Field label={tr(F.trickType)}>
            <BareInput
              aria-label={tr(F.trickType)}
              value={block.trickType ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ trickType: v.trim() || undefined })}
            />
          </Field>
          <Field label={tr(F.hint)} wide>
            <BareInput
              aria-label={tr(F.hint)}
              value={block.prompt ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ prompt: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "phoneText":
      return (
        <FieldGrid>
          <Field label={tr(F.direction)}>
            <Select
              aria-label={tr(F.direction)}
              value={block.direction ?? "from"}
              options={[
                { value: "from", label: tr(F.from) },
                { value: "to", label: tr(F.to) },
              ]}
              disabled={disabled}
              onSelect={(v) => onPatch({ direction: v as "from" | "to" })}
            />
          </Field>
          <Field label={tr(F.role)}>
            <BareInput
              aria-label={tr(F.role)}
              value={block.character ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ character: v.trim().toUpperCase() || undefined })}
            />
          </Field>
          <Field label={tr(F.text)} wide>
            <BareTextarea
              aria-label={tr(F.text)}
              value={block.text ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ text: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "minigame":
      return (
        <FieldGrid>
          <Field label={tr(F.template)}>
            <Select
              aria-label={tr(F.template)}
              value={MINIGAME_PRESETS.some((preset) => preset.id === block.name) ? (block.name ?? "") : ""}
              options={[
                { value: "", label: tr(F.customTemplate) },
                ...MINIGAME_PRESETS.map((preset) => ({ value: preset.id, label: tr(preset.label) })),
              ]}
              disabled={disabled}
              onSelect={(v) => {
                const preset = MINIGAME_PRESETS.find((item) => item.id === v);
                if (preset) onPatch({ name: preset.id, description: preset.description });
              }}
            />
          </Field>
          <Field label={tr(F.minigameName)}>
            <BareInput
              aria-label={tr(F.minigameName)}
              value={block.name ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ name: v.trim() || undefined })}
            />
          </Field>
          <Field label={tr(F.desc)} wide>
            <BareInput
              aria-label={tr(F.desc)}
              value={block.description ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ description: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "butterfly":
      return (
        <FieldGrid>
          <Field label={tr(F.memory)} wide>
            <BareTextarea
              aria-label={tr(F.memory)}
              value={block.description ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ description: v })}
            />
          </Field>
        </FieldGrid>
      );

    case "achievement":
      return (
        <FieldGrid>
          <Field label={tr(F.achId)} wide>
            <BareInput
              aria-label={tr(F.achId)}
              value={block.id ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ id: v.trim() || undefined })}
            />
          </Field>
        </FieldGrid>
      );

    // ── @episode header — title + branch key are safely single-line editable ──
    case "episode":
      return (
        <FieldGrid>
          <Field label={tr(F.title)} wide>
            <BareInput
              aria-label={tr(F.title)}
              value={block.title ?? ""}
              disabled={disabled}
              onCommit={(v) => onPatch({ title: v })}
            />
          </Field>
          <Field label={tr(F.branch)}>
            <BareInput
              aria-label={tr(F.branch)}
              value={block.branchKey ?? ""}
              placeholder="main:01"
              disabled={disabled}
              onCommit={(v) => onPatch({ branchKey: v.trim() || undefined })}
            />
          </Field>
        </FieldGrid>
      );

    // ── key: value metadata (检定 attr/dc, 成就 name/rarity/description) ──
    case "param":
      return (
        <FieldGrid>
          <Field label={tr(F.paramKey)}>
            <BareInput
              aria-label={tr(F.paramKey)}
              value={block.key}
              disabled={disabled}
              onCommit={(v) => onPatch({ key: v.trim() || block.key })}
            />
          </Field>
          <Field label={tr(F.paramValue)} wide>
            <BareInput
              aria-label={tr(F.paramValue)}
              value={block.value}
              disabled={disabled}
              onCommit={(v) => onPatch({ value: v })}
            />
          </Field>
        </FieldGrid>
      );

    // ── @if / @else — edit the condition (all forms) + the route (gate form) ──
    case "if": {
      const isBodyForm = block.raw.trim().endsWith("{");
      return (
        <FieldGrid>
          <Field label={tr(F.condition)} wide>
            <BareInput
              aria-label={tr(F.condition)}
              value={block.condition ?? ""}
              placeholder={block.isElse ? "" : "affection.rin >= 2"}
              disabled={disabled}
              onCommit={(v) => onPatch({ condition: v.trim() || undefined })}
            />
          </Field>
          {isBodyForm ? null : (
            <>
              <Field label={tr(F.routeTo)}>
                <BareInput
                  aria-label={tr(F.routeTo)}
                  value={block.next ?? ""}
                  placeholder="main:02"
                  disabled={disabled}
                  onCommit={(v) => onPatch({ next: v.trim() || undefined, end: undefined })}
                />
              </Field>
              <Field label={tr(F.endType)}>
                <Select
                  aria-label={tr(F.endType)}
                  value={block.end ?? ""}
                  options={[{ value: "", label: "—" }, ...END_TYPES.map((t) => ({ value: t, label: t }))]}
                  disabled={disabled}
                  onSelect={(v) => (v ? onPatch({ end: v, next: undefined }) : onPatch({ end: undefined }))}
                />
              </Field>
            </>
          )}
        </FieldGrid>
      );
    }

    // ── @gate — only the INLINE `@gate { @next X }` form is field-editable ──
    case "gate": {
      if (!block.raw.includes("}")) {
        return gate ? <GateCardPreview gate={gate} choices={allChoices} /> : <p className="lc-block__content">{describeBlock(block)}</p>;
      }
      return (
        <>
          {gate ? <GateCardPreview gate={gate} choices={allChoices} /> : null}
          <FieldGrid>
            <Field label={tr(F.routeTo)}>
              <BareInput
                aria-label={tr(F.routeTo)}
                value={block.next ?? ""}
                placeholder="main:02"
                disabled={disabled}
                onCommit={(v) => onPatch({ next: v.trim() || undefined, end: undefined })}
              />
            </Field>
            <Field label={tr(F.endType)}>
              <Select
                aria-label={tr(F.endType)}
                value={block.end ?? ""}
                options={[{ value: "", label: "—" }, ...END_TYPES.map((t) => ({ value: t, label: t }))]}
                disabled={disabled}
                onSelect={(v) => (v ? onPatch({ end: v, next: undefined }) : onPatch({ end: undefined }))}
              />
            </Field>
          </FieldGrid>
        </>
      );
    }

    // Compound openers / structural / unknown — read-only summary only.
    default: {
      if (choice) return <ChoiceCardPreview choice={choice} />;
      const summary = SUMMARY[block.kind];
      return <p className="lc-block__content">{summary ? tr(summary) : describeBlock(block)}</p>;
    }
  }
}

export function BlockFieldsEditor({
  source,
  block,
  onChange,
  readOnly,
  choice,
  option,
  gate,
  allChoices,
}: {
  readonly source: string;
  readonly block: LsBlock;
  readonly onChange: (source: string) => void;
  readonly readOnly?: boolean;
  readonly choice?: ChoiceStructure;
  readonly option?: ChoiceOptionStructure;
  readonly gate?: GateStructure;
  readonly allChoices?: readonly ChoiceStructure[];
}) {
  return (
    <BlockBody
      block={block}
      readOnly={readOnly}
      choice={choice}
      option={option}
      gate={gate}
      allChoices={allChoices}
      onPatch={(patch) => onChange(patchBlock(source, block, patch))}
    />
  );
}

// ── inline field primitives ──────────────────────────────────────────────────

/** A labelled cell in the field grid. */
function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={wide ? "lc-block__field lc-block__field--wide" : "lc-block__field"}>
      <span className="lc-block__field-label">{label}</span>
      {children}
    </div>
  );
}

/** The responsive grid that lays out a block's inline editors. */
function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="lc-block__fields">{children}</div>;
}

interface CommitInputProps {
  readonly value: string;
  readonly onCommit: (value: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly "aria-label"?: string;
}

/**
 * A single-line editor that commits on every keystroke (so the card view stays
 * live-bound to the source). Wraps the DS TextInput, unlabelled. Controlled by
 * `value`; edits flow up via `onCommit`.
 */
function BareInput({ value, onCommit, placeholder, disabled, ...aria }: CommitInputProps) {
  return (
    <TextInput
      className="lc-block__input"
      aria-label={aria["aria-label"]}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      spellCheck={false}
      onChange={(e) => onCommit(e.target.value)}
    />
  );
}

/** A multi-line editor for longer text (dialogue / narration bodies). */
function BareTextarea({ value, onCommit, disabled, ...aria }: Omit<CommitInputProps, "placeholder">) {
  return (
    <Textarea
      className="lc-block__input"
      aria-label={aria["aria-label"]}
      value={value}
      rows={2}
      disabled={disabled}
      spellCheck={false}
      onChange={(e) => onCommit(e.target.value)}
    />
  );
}

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

interface SelectProps {
  readonly value: string;
  readonly options: ReadonlyArray<SelectOption>;
  readonly onSelect: (value: string) => void;
  readonly disabled?: boolean;
  readonly "aria-label"?: string;
}

/** A compact native select styled to the cream inputs (used for enums). */
function Select({ value, options, onSelect, disabled, ...aria }: SelectProps) {
  return (
    <select
      className="lc-block__select"
      aria-label={aria["aria-label"]}
      value={value}
      disabled={disabled}
      onChange={(e) => onSelect(e.target.value)}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

/** Transition enum select with an explicit none sentinel. */
function TransitionSelect({
  value,
  onSelect,
  disabled,
}: {
  value?: string;
  onSelect: (value: string | undefined) => void;
  disabled?: boolean;
}) {
  const tr = useTr();
  return (
    <select
      className="lc-block__select"
      aria-label={tr(F.transition)}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onSelect(e.target.value || undefined)}
    >
      <option value="">{tr(F.transitionNone)}</option>
      {TRANSITIONS.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  );
}

/**
 * Reduce a parsed block to a single human-readable English summary line. Falls
 * back to the raw source when a kind has no richer projection, so a row is never
 * blank. Exported for unit testing the projection and reused as the read-only
 * body of non-editable (compound / structural) block rows.
 */
export function describeBlock(block: LsBlock): string {
  switch (block.kind) {
    case "episode":
      return [block.title ? `「${block.title}」` : null, block.branchKey ? `Branch ${block.branchKey}` : null]
        .filter(Boolean)
        .join(" · ") || block.raw;
    case "bg":
      return [block.name, block.transition].filter(Boolean).join(" · ") || block.raw;
    case "char":
      return [
        block.character,
        block.bubble ? `Bubble${block.pose ? ` ${block.pose}` : ""}` : block.pose,
        block.transition,
      ]
        .filter(Boolean)
        .join(" · ");
    case "dialogue":
      return `${block.speaker}${block.pose ? `（${block.pose}）` : ""}：${block.text}`;
    case "narration":
      return `${block.speaker === "YOU" ? "Thought" : "Narration"}：${block.text}`;
    case "cg":
      return [block.name, block.content ? `「${block.content}」` : null].filter(Boolean).join(" · ") || block.raw;
    case "music":
      return block.stop ? "Stop music" : block.name ?? block.raw;
    case "sfx":
      return block.name ?? block.raw;
    case "pause":
      return "Wait for player to continue";
    case "choice":
      return "Player choice";
    case "option":
      return [
        block.id,
        block.mode === "brave" ? "Brave" : block.mode === "safe" ? "Safe" : null,
        block.text ? `「${block.text}」` : null,
      ]
        .filter(Boolean)
        .join(" · ") || block.raw;
    case "check":
      return "D20 check";
    case "if":
      return block.isElse
        ? block.condition
          ? `Else if (${block.condition})`
          : "Else"
        : block.condition
          ? `If (${block.condition})`
          : block.raw;
    case "gate":
      return [block.next ? `Go to ${block.next}` : null, block.end ? `End ${block.end}` : null]
        .filter(Boolean)
        .join(" · ") || "Route";
    case "affection":
      return [block.character, block.delta].filter(Boolean).join(" ") || block.raw;
    case "signal":
      if (block.signalKind === "int") {
        return [block.name, block.op, block.value].filter(Boolean).join(" ") || block.raw;
      }
      return block.name ? `Flag ${block.name}` : block.raw;
    case "trick":
      return [block.trickType, block.prompt ? `「${block.prompt}」` : null].filter(Boolean).join(" · ") || block.raw;
    case "phone":
      return "Phone messages";
    case "phoneText":
      return [
        block.direction === "to" ? "Sent" : "Received",
        block.character,
        block.text ? `：${block.text}` : null,
      ]
        .filter(Boolean)
        .join(" ") || block.raw;
    case "minigame":
      return [block.name, block.description ? `「${block.description}」` : null].filter(Boolean).join(" · ") || block.raw;
    case "achievement":
      return block.id ? `Achievement ${block.id}` : block.raw;
    case "butterfly":
      return block.description ? `Memory: ${block.description}` : block.raw;
    default:
      return block.raw;
  }
}
